import { gql, useQuery } from '@apollo/client'

// Resolves the /participation/:id route param (an 8-char slug OR a full session id) to a valid
// participation-session id. The backend resolver validates BOTH forms — a slug must be the
// session's current slug, and a raw session id is only valid if the session was never slug-managed
// (once a session has a slug, the old-style id link is disabled too). So we always ask the server
// rather than passing session-id links through.
const PARTICIPATION_SESSION_ID_BY_SLUG = gql`
  query ParticipationSessionIdBySlug($decisionId: ID!, $slug: String!) {
    participationSessionIdBySlug(decisionId: $decisionId, slug: $slug)
  }
`

interface ResolvedParticipationSessionId {
  /** The resolved participation-session id, or undefined while loading / when the link is invalid. */
  participationSessionId?: string
  loading: boolean
  /** True when the link didn't resolve — unknown, rotated, or an old-style id link that's now disabled. */
  notFound: boolean
}

/** Identifies the signed-in user to the authenticated GraphQL endpoint (see below). */
interface ParticipationSessionIdAuth {
  accountId: string
  authUserId: string
}

/**
 * Resolves a /participation/:id route param to a participation-session id, or reports notFound when
 * the link is no longer valid. Works both authenticated (facilitator) and unauthenticated (ad-hoc
 * participant) — the Apollo client routes to the auth/unauth endpoint automatically, based on
 * whether there's a current Cognito session.
 *
 * Pass `auth` whenever the caller renders for a signed-in user: the request will go to the
 * authenticated endpoint, and AuthContext.fromEvent rejects any request there that's missing the
 * `vms-accountid` / `vms-userid` headers (Apollo's link only adds the JWT). Without them the query
 * errors out and every participation link looks like a dead link. Omit `auth` for the ad-hoc
 * participant flow, which has no account context and hits the unauth endpoint.
 */
export function useResolvedParticipationSessionId(
  decisionId: string,
  idOrSlug: string,
  auth?: ParticipationSessionIdAuth
): ResolvedParticipationSessionId {
  // network-only so a link that was rotated in another session isn't served as valid from cache.
  const { data, loading, error } = useQuery(PARTICIPATION_SESSION_ID_BY_SLUG, {
    variables: { decisionId, slug: idOrSlug },
    fetchPolicy: 'network-only',
    context: auth
      ? { headers: { 'vms-accountid': auth.accountId, 'vms-userid': auth.authUserId } }
      : undefined,
  })

  // A failed request isn't a rotated link; don't leave it silently indistinguishable from one.
  if (error) console.error(`error resolving participation link "${idOrSlug}":`, error)

  const resolved = (data?.participationSessionIdBySlug as string | null | undefined) ?? undefined
  return {
    participationSessionId: resolved,
    loading,
    notFound: !loading && !resolved,
  }
}
